import React, { useState } from "react";
import * as Icons from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { SECTORS, SERVICES, MANPOWER } from "../data/content";

export const Services = () => {
  const [activeTab, setActiveTab] = useState("all");

  const combinedServices = [
    ...SERVICES.map((s) => ({ ...s, type: "inspection" })),
    ...MANPOWER.map((m) => ({ ...m, type: "manpower" })),
  ];

  const filteredServices =
    activeTab === "all"
      ? combinedServices
      : activeTab === "inspection"
      ? SERVICES.map((s) => ({ ...s, type: "inspection" }))
      : MANPOWER.map((m) => ({ ...m, type: "manpower" }));

  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-[#F8FAFC] py-24 lg:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
          <Reveal className="max-w-3xl">
            <SectionLabel>Our Services & Competencies</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight text-[#0A192F]">
              Global Inspection, NDT Consultancy & Engineering Manpower
            </h2>
          </Reveal>

          {/* Service Category Filter Tabs */}
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-2 p-1.5 bg-slate-200/80 rounded-lg">
              <button
                type="button"
                onClick={() => setActiveTab("all")}
                className={`px-4 py-2 text-xs sm:text-sm font-semibold rounded-md transition-all duration-300 ${
                  activeTab === "all"
                    ? "bg-[#0A192F] text-white shadow-sm"
                    : "text-slate-700 hover:text-[#0A192F]"
                }`}
              >
                All Services
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("inspection")}
                className={`px-4 py-2 text-xs sm:text-sm font-semibold rounded-md transition-all duration-300 ${
                  activeTab === "inspection"
                    ? "bg-[#0A192F] text-white shadow-sm"
                    : "text-slate-700 hover:text-[#0A192F]"
                }`}
              >
                Inspection & NDT
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("manpower")}
                className={`px-4 py-2 text-xs sm:text-sm font-semibold rounded-md transition-all duration-300 ${
                  activeTab === "manpower"
                    ? "bg-[#0A192F] text-white shadow-sm"
                    : "text-slate-700 hover:text-[#0A192F]"
                }`}
              >
                Manpower & Staffing
              </button>
            </div>
          </Reveal>
        </div>

        {/* Services Grid with Icons */}
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredServices.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.CircleDot;
            const isNdt = s.title.toLowerCase().includes("ndt");
            return (
              <Reveal key={s.title} delay={i * 0.05}>
                <div
                  data-testid={`service-card-${i}`}
                  className="group relative h-full bg-white p-8 lg:p-9 border border-slate-200/80 rounded-lg hover:border-[#D4AF37] hover:shadow-xl transition-all duration-300 flex flex-col justify-between overflow-hidden"
                >
                  <div className="absolute top-0 left-0 right-0 h-1 bg-transparent group-hover:bg-[#D4AF37] transition-all duration-300" />
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="grid h-12 w-12 place-items-center rounded-md bg-[#0A192F] text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-[#0A192F] transition-colors duration-300">
                        <Icon size={22} />
                      </div>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 bg-slate-100 px-2.5 py-1 rounded">
                        {s.type === "inspection" ? "Inspection" : "Manpower"}
                      </span>
                    </div>

                    <h3 className="mt-6 font-display text-lg font-bold leading-snug text-[#0A192F] group-hover:text-[#0A192F] transition-colors duration-300 flex items-center gap-2">
                      {s.title}
                      {isNdt && (
                        <span className="inline-block text-[10px] font-black uppercase px-2 py-0.5 bg-[#D4AF37]/20 text-[#0A192F] rounded border border-[#D4AF37]/40">
                          Core
                        </span>
                      )}
                    </h3>

                    <p className="mt-3 text-sm leading-relaxed text-slate-600">
                      {s.body}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-slate-100 flex items-center gap-1.5 text-xs font-semibold text-[#0A192F] group-hover:text-[#D4AF37] transition-colors duration-300">
                    <span>Learn More</span>
                    <Icons.ChevronRight size={14} />
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Sectors List */}
        <Reveal delay={0.1}>
          <div className="mt-20 border-t border-slate-200 pt-12">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="font-display text-xs font-bold uppercase tracking-[0.24em] text-slate-500">
                  Target Industries & Sectors Served
                </p>
                <h4 className="mt-1 text-xl font-bold text-[#0A192F]">
                  Providing Expertise Across High-Precision Engineering Sectors
                </h4>
              </div>
            </div>

            <div
              className="mt-8 flex flex-wrap gap-3"
              data-testid="sectors-list"
            >
              {SECTORS.map((s) => {
                const Icon = Icons[s.icon] || Icons.CircleDot;
                return (
                  <div
                    key={s.name}
                    className="flex items-center gap-2.5 rounded-full border border-slate-200 bg-white px-5 py-2.5 hover:border-[#D4AF37] hover:bg-[#0A192F] hover:text-white group transition-all duration-300 shadow-sm"
                  >
                    <Icon
                      size={16}
                      className="text-[#D4AF37] group-hover:text-[#D4AF37]"
                    />
                    <span className="text-sm font-medium text-[#0A192F] group-hover:text-white transition-colors duration-300">
                      {s.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

